import {
  getPerfectDashProps,
  OverlayUtil,
  PI2,
  Vec
} from "@tldraw/editor";
import { getArrowInfo } from "../shapes/arrow/getArrowInfo.mjs";
import { getArrowBindings } from "../shapes/arrow/shared.mjs";
class ArrowBindingHintOverlayUtil extends OverlayUtil {
  static type = "arrow_binding_hint";
  options = {
    zIndex: 150,
    strokeWidth: 2,
    opacity: 0.16,
    dashLengthRatio: 2.5,
    dotRadius: 4,
    crossSize: 6,
    dashedMinZoom: 0.2
  };
  isActive() {
    const editor = this.editor;
    if (editor.getIsReadonly()) return false;
    const id = editor.getOnlySelectedShapeId();
    if (!id) return false;
    const shape = editor.getShape(id);
    if (!shape || shape.type !== "arrow") return false;
    if (!editor.isInAny(
      "select.idle",
      "select.pointing_handle",
      "select.dragging_handle",
      "select.translating",
      "arrow.dragging"
    )) {
      return false;
    }
    const bindings = getArrowBindings(editor, shape);
    return Boolean(bindings.start || bindings.end);
  }
  getOverlays() {
    const id = this.editor.getOnlySelectedShapeId();
    if (!id) return [];
    return [
      {
        id: "arrow_binding_hint",
        type: "arrow_binding_hint",
        props: { arrowId: id }
      }
    ];
  }
  render(ctx, overlays) {
    const overlay = overlays[0];
    if (!overlay) return;
    const editor = this.editor;
    const shape = editor.getShape(overlay.props.arrowId);
    if (!shape) return;
    const info = getArrowInfo(editor, shape);
    if (!info?.isValid) return;
    const bindings = getArrowBindings(editor, shape);
    if (!bindings.start && !bindings.end) return;
    const pageTransform = editor.getShapePageTransform(shape);
    if (!pageTransform) return;
    const zoom = editor.getZoomLevel();
    const colors = editor.getCurrentTheme().colors[editor.getColorMode()];
    const strokeWidth = this.options.strokeWidth / zoom;
    ctx.save();
    ctx.transform(
      pageTransform.a,
      pageTransform.b,
      pageTransform.c,
      pageTransform.d,
      pageTransform.e,
      pageTransform.f
    );
    ctx.strokeStyle = colors.text;
    ctx.fillStyle = colors.text;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.globalAlpha = this.options.opacity;
    if (bindings.start) {
      this.drawEndpoint(
        ctx,
        info,
        "start",
        bindings.start.props.isExact,
        bindings.start.props.isPrecise,
        strokeWidth,
        zoom
      );
    }
    if (bindings.end) {
      this.drawEndpoint(
        ctx,
        info,
        "end",
        bindings.end.props.isExact,
        bindings.end.props.isPrecise,
        strokeWidth,
        zoom
      );
    }
    ctx.restore();
  }
  drawEndpoint(ctx, info, side, isExact, isPrecise, strokeWidth, zoom) {
    const handle = info[side].handle;
    const point = info[side].point;
    const dist = Vec.Dist(handle, point);
    const markerRadius = (isPrecise ? this.options.crossSize / 2 * Math.SQRT2 : this.options.dotRadius) / zoom;
    if (dist > markerRadius + 0.5) {
      ctx.save();
      ctx.beginPath();
      let pathLength;
      if (info.type === "arc") {
        const { center, radius, sweepFlag } = info.bodyArc;
        const pointAngle = Math.atan2(point.y - center.y, point.x - center.x);
        const handleAngle = Math.atan2(handle.y - center.y, handle.x - center.x);
        const anticlockwise = !sweepFlag;
        const sign = (sweepFlag ? 1 : -1) * (side === "start" ? 1 : -1);
        const trimmedHandleAngle = handleAngle + sign * (markerRadius / radius);
        let handleToPointSweep = pointAngle - handleAngle;
        if (handleToPointSweep > Math.PI) handleToPointSweep -= PI2;
        else if (handleToPointSweep < -Math.PI) handleToPointSweep += PI2;
        pathLength = Math.max(0, radius * Math.abs(handleToPointSweep) - markerRadius);
        if (side === "start") {
          ctx.arc(center.x, center.y, radius, trimmedHandleAngle, pointAngle, anticlockwise);
        } else {
          ctx.arc(center.x, center.y, radius, pointAngle, trimmedHandleAngle, anticlockwise);
        }
      } else {
        pathLength = dist - markerRadius;
        const t = markerRadius / dist;
        const trimmedHandle = {
          x: handle.x + (point.x - handle.x) * t,
          y: handle.y + (point.y - handle.y) * t
        };
        ctx.moveTo(trimmedHandle.x, trimmedHandle.y);
        ctx.lineTo(point.x, point.y);
      }
      if (zoom >= this.options.dashedMinZoom) {
        const { strokeDasharray, strokeDashoffset } = getPerfectDashProps(pathLength, strokeWidth, {
          style: "dashed",
          end: "skip",
          start: "skip"
        });
        if (strokeDasharray !== "none") {
          const [dashLength, gapLength] = strokeDasharray.split(" ").map(Number);
          ctx.setLineDash([dashLength, gapLength]);
          ctx.lineDashOffset = Number(strokeDashoffset);
        }
      }
      ctx.stroke();
      ctx.restore();
    }
    if (isExact) return;
    const markerAngle = dist > 0.5 ? this.getMarkerAngle(info, side, handle, point) : 0;
    if (isPrecise) {
      const half = this.options.crossSize / zoom / 2;
      ctx.save();
      ctx.translate(handle.x, handle.y);
      ctx.rotate(markerAngle);
      ctx.beginPath();
      ctx.moveTo(-half, -half);
      ctx.lineTo(half, half);
      ctx.moveTo(-half, half);
      ctx.lineTo(half, -half);
      ctx.stroke();
      ctx.restore();
    } else {
      const radius = this.options.dotRadius / zoom;
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, radius, 0, PI2);
      ctx.stroke();
    }
  }
  /** Tangent direction at the handle, oriented toward the body. */
  getMarkerAngle(info, side, handle, point) {
    if (info.type === "arc") {
      const { center, sweepFlag } = info.bodyArc;
      const radial = Math.atan2(handle.y - center.y, handle.x - center.x);
      const sign = (sweepFlag ? 1 : -1) * (side === "start" ? 1 : -1);
      return radial + sign * Math.PI / 2;
    }
    return Math.atan2(point.y - handle.y, point.x - handle.x);
  }
}
export {
  ArrowBindingHintOverlayUtil
};
//# sourceMappingURL=ArrowBindingHintOverlayUtil.mjs.map
